import React, { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Anchor, Box, getDuration, getValidationResolver, Preloader, Text } from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { ExternalLink, REQUIRED_TEXT } from '@/common/constants';
import ROUTES from '@/common/routes';
import { WarnModal } from '@/components/elements';
import balanceReminderSessionStorage from '@/modules/core/components/Dashboard/components/BalanceReminderBanners/balanceReminderSessionStorage';
import { useHandleUnconfirmedUserError } from '@/modules/user/components/SignIn/hooks';
import { AccountLockoutModal } from '@/modules/user/components/SignIn/MultiFactorAuthentication/components';
import {
  MFAChannel,
  MFAChannelType,
  MfaServerErrorText,
} from '@/modules/user/components/SignIn/MultiFactorAuthentication/mfa.types';
import useMFAStore from '@/modules/user/components/SignIn/MultiFactorAuthentication/stores/useMFA.store';
import { SignInServerErrorText } from '@/modules/user/components/SignIn/signIn.types';
import { SignInErrorCode } from '@/modules/user/components/SignUp/signUp.constants';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';

import usePartnerLoginConfig from './hooks/usePartnerLoginConfig';
import { LoginPayload } from './hooks/useSignIn';
import MultiFactorAuthenticationModals from './MultiFactorAuthentication/MultiFactorAuthenticationModals';
import { useResendCodeTimerStore } from './stores/useResendCodeTimer.store';
import { useSignIn } from './hooks';
import {
  Form,
  FormActions,
  FormFooter,
  FormHelpText,
  SignInTextInput,
  SinInAppButton,
} from './SignInForm.styles';

const ACCOUNT_LOCKED_MINIMUM_TIME = 1;

const getDefaultChannel = (channels: MFAChannel[]) =>
  channels?.find((channel) => channel.isDefault) ||
  channels?.find((channel) => channel.type === MFAChannelType.EMAIL) ||
  channels?.[0];

interface SignInProps {
  disabled?: boolean;
  onForgot: () => void;
  children?: React.ReactNode;
  isChangePasswordMode?: boolean;
  onForgotUsername?: () => void;
}

const SignIn: React.FC<SignInProps> = ({ disabled, children, onForgot, onForgotUsername }) => {
  const { t } = useTranslation();
  const { handleUnconfirmedUserError } = useHandleUnconfirmedUserError();
  const { setActiveChannel, setChannels, setSession, setUserName } = useMFAStore();
  const [isMFAModalVisible, setIsMFAModalVisible] = useState(false);
  const [isAccountLockedModalVisible, setIsAccountLockedModalVisible] = useState(false);
  const [lockingLeft, setLockingLeft] = useState<number>();
  const [isSingInError, setIsSingInError] = useState(false);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
    },
    resolver: getValidationResolver({
      username: yup.string().required(REQUIRED_TEXT),
      password: yup.string().required(REQUIRED_TEXT),
    }),
  });

  const { partnerLoginConfig } = usePartnerLoginConfig();

  const handleShowAccountLockedModal = useCallback((lockingLeft: number) => {
    setIsMFAModalVisible(false);
    setIsAccountLockedModalVisible(true);
    setLockingLeft(lockingLeft || ACCOUNT_LOCKED_MINIMUM_TIME);
  }, []);

  const { handleLogin } = useSignIn();
  const { setTimer } = useResendCodeTimerStore();

  const onSubmit = async (data: LoginPayload) => {
    setErrorText(undefined);
    try {
      setIsLoading(true);
      const authResponse = await handleLogin(data);
      balanceReminderSessionStorage.clear();
      const channels = authResponse?.channels;
      if (channels?.length) {
        const defaultChannel = getDefaultChannel(channels);
        setActiveChannel(defaultChannel);
        setChannels(channels);
        setSession(authResponse?.session || '');
        setUserName(data?.username);
        setIsMFAModalVisible(true);
      }
      setIsSingInError(false);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);

      const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;
      const authErrorCode = _.get(error, 'response.data.auth_error_code', '') as string;
      // @ts-ignore
      const lockingLeft = error?.response?.data?.locking_left;

      if (
        errorMessage.includes(MfaServerErrorText.USER_ACCOUNT_IS_LOCKED) &&
        typeof lockingLeft === 'number'
      ) {
        handleShowAccountLockedModal(lockingLeft);
        setIsSingInError(false);
        return;
      }

      if (authErrorCode === SignInErrorCode.UNCONFIRMED_USER_ERROR_CODE) {
        setTimer({ expiredIn: `${Date.now() + getDuration({ minutes: 1 })}` });
        handleUnconfirmedUserError({ username: data?.username });
        setIsSingInError(false);
        return;
      }

      if (authErrorCode === SignInErrorCode.ATTEMPTS_LIMIT_ERROR_CODE) {
        setErrorText('Too many failed login attempts. Please try again in a few minutes');
        setIsSingInError(false);
        return;
      }

      if (errorMessage.includes(SignInServerErrorText.CREDENTIALS_ARE_INCORRECT)) {
        setErrorText('Username or password is wrong');
        setIsSingInError(false);
        return;
      }

      if (errorMessage.includes(SignInServerErrorText.USER_ACCOUNT_IS_LOCKED) && lockingLeft === null) {
        setErrorText('Username or password is wrong');
        setIsSingInError(false);
        return;
      }

      setIsSingInError(true);
    }
  };

  if (partnerLoginConfig) {
    return (
      <Box width={{ width: '100%', max: '220px' }} alignSelf="center">
        <SinInAppButton
          disabled={disabled}
          width="100%"
          className="button"
          type="submit"
          size="L"
          onClick={() => {
            window.location.href = partnerLoginConfig.loginUrl;
          }}
        >
          {t(partnerLoginConfig.buttonText)}
        </SinInAppButton>
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Box>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Text
              size="large"
              weight="bold"
              margin={{ bottom: 'spacing12' }}
              style={{
                lineHeight: '22px',
              }}
            >
              Username
            </Text>
            <Controller
              // @ts-ignore
              name="username"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Box margin={{ bottom: 'spacing24' }}>
                  <SignInTextInput
                    className="text-field"
                    value={value}
                    isLarge
                    onChange={onChange}
                    onBlur={() => {
                      onChange(value?.trim());
                    }}
                    name="username"
                    enterKeyHint="next"
                    label={t('Username')}
                    placeholder={t('Username')}
                    error={!!errors.username?.message || !!errorText}
                    testId="username_input"
                    isLowerCaseMode
                  />
                </Box>
              )}
            />
            <Text
              size="large"
              weight="bold"
              margin={{ bottom: 'spacing12' }}
              style={{
                lineHeight: '22px',
              }}
            >
              Password
            </Text>
            <Controller
              // @ts-ignore
              name="password"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Box margin={{ bottom: 'spacing24' }}>
                  <SignInTextInput
                    className="text-field"
                    value={value}
                    isLarge
                    onChange={onChange}
                    enterKeyHint="go"
                    label={t('Password')}
                    placeholder={t('Password')}
                    inputType="password"
                    name="password"
                    testId="password_input"
                    errorText={errors.password?.message || errorText}
                  />
                </Box>
              )}
            />
            {children}
            <SinInAppButton
              disabled={disabled || isLoading}
              width="100%"
              className="button"
              type="submit"
              size="L"
            >
              {isLoading ? <Preloader color="white" /> : t('Sign In')}
            </SinInAppButton>
          </Form>
        </Box>
        <FormHelpText>
          {t('By clicking Sign In, you agree to')}{' '}
          <Anchor
            target="_blank"
            href={whiteLabelingConfig?.termsAndConditions || ExternalLink.termsAndCondition}
          >
            {t('Terms and Conditions')}
          </Anchor>{' '}
          {t('and have read and acknowledged our')}{' '}
          <Anchor target="_blank" href={whiteLabelingConfig?.privacyPolicy || ExternalLink.privacyPolicy}>
            {t('Privacy Policy.')}
          </Anchor>
        </FormHelpText>
        <FormActions>
          <Anchor
            size="large"
            disabled={disabled}
            weight="bold"
            data-testid="auth_forgot-username_button"
            onClick={onForgotUsername}
          >
            {t('Forgot Username?')}
          </Anchor>
          <Box
            margin={{ horizontal: 'spacing8' }}
            background="border"
            width={{ min: '4px' }}
            height={{ min: '4px' }}
            round="100%"
          />
          <Anchor
            type="button"
            data-testid="button-forgot-password"
            // name="forgot_password"
            onClick={onForgot}
            size="large"
            weight="bold"
            disabled={disabled}
            style={{
              height: '22px',
            }}
          >
            {t('Forgot Password?')}
          </Anchor>
          {/* <RedirectButton type="button" onClick={() => {}}>
            {t('Forgot Username?')}
          </RedirectButton> */}
        </FormActions>
        <FormFooter>
          <Text style={{ fontSize: 'inherit' }}>{t('Need an account? ')}</Text>
          {disabled ? (
            <Anchor size="large" disabled weight="bold">
              {t('Register')}
            </Anchor>
          ) : (
            <Link to={ROUTES.SIGN_UP}>
              <Anchor size="large" weight="bold">
                {t('Register')}
              </Anchor>
            </Link>
          )}
        </FormFooter>
      </Box>
      {isMFAModalVisible && (
        <MultiFactorAuthenticationModals
          onSetVisible={setIsMFAModalVisible}
          onShowAccountLockedModal={handleShowAccountLockedModal}
        />
      )}
      {isAccountLockedModalVisible && (
        <AccountLockoutModal onSetVisible={setIsAccountLockedModalVisible} lockingLeft={lockingLeft} />
      )}
      {isSingInError && (
        <WarnModal
          testId="sign-in_error"
          visible={isSingInError}
          onSetVisible={setIsSingInError}
          header={t('The server encountered an error processing the request')}
          helptext={t('Some technical issues occur during login. Check your credentials and try again.')}
          buttonText={t('Close')}
          submitButtonText={t('Try Again')}
          onSubmit={handleSubmit(onSubmit)}
          isAwaitMode
          disableAutomaticClosing
        />
      )}
    </>
  );
};

export default SignIn;
